import { drillEvaluationScore } from '@felix/domain';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { type ReportFiles, reportAttachmentSection } from '../../attachments/report-files.js';
import { formatDay } from '../../format.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { sha256Hex, zipStore } from '../fuel/period-package.js';
import { renderPdf } from '../personnel/pdf.js';
import {
  pdfBlock,
  pdfFacts,
  pdfHeading,
  pdfTable,
  pdfText,
  type RecordPdfApproval,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import type { DrillRecordDetail } from './record-types.js';
import { formatSeconds } from './record-view.js';

/**
 * Tatbikat raporu (ortağın belgesi §8 "Drill Records & Reports"; ADR 0128 Faz 5 Ek): kaydın kendi
 * içeriğinden, ortak kayıt PDF kabuğunda (ADR 0062 §3) Part A–E:
 *
 * - **A General Information**: gemi, tarih, saat ve süre, yer, tatbikat ve türü, sorumlu, senaryo.
 * - **B Preparation & Execution**: hazırlık, kontrol listesi, katılımcılar, adımlar ve zamanları.
 * - **C Evaluation**: kriterler (süreler ve değerlendirme), gözlem, puan (şirket yöntemi), kişi
 *   performansı ve eksiklikler.
 * - **D Corrective Actions**: bulgular, bağlı arıza / NCR / iş emri / eğitim ihtiyacı ve takip
 *   durumu, arıza zinciri.
 * - **E Approval**: sorumlu zabitin gönderimi, kaptan incelemesi (vekil "on behalf of Master";
 *   ADR 0127), elektronik onay (içerik hash'i) ve zaman (LT + UTC).
 *
 * Ardından şablon ve sürüm (§13.1), ekler (resimler gömülü, diğer dosyalar bağlantı; ADR 0130 §A)
 * ve değişiklik geçmişi
 * (denetim izi, §13.6). pdfmake istemcide, gömülü yazı tipiyle internetsiz çalışır; dil arayüzün
 * dilidir (HSE raporu emsali).
 */

export interface DrillReportInput {
  record: DrillRecordDetail;
  brand: ReportBrand | null;
  /** Basım zamanı, UTC ISO. */
  printedAt: string;
  /** Eklerin gömülü resimleri ve bağlantıları (ADR 0130 §A); yoksa yalnız adlar. */
  files?: ReportFiles;
}

const shortHash = (hash: string | null | undefined) => (hash ? hash.slice(0, 12) : '—');
const utc = (iso: string) => `${zonedStamp(iso)} UTC`;

function marks(t: RecordPdfT, record: DrillRecordDetail): string[] {
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`drillReport.marks.${key}`, options);
  switch (record.state) {
    case 'approved':
      return [];
    case 'stopped':
      return [k('stopped')];
    case 'cancelled':
      return [k('cancelled')];
    default:
      return [
        ...(record.reopened ? [k('reopened')] : []),
        k('notApproved', { state: t(`drillRecord.state.${record.state}`).toUpperCase() }),
      ];
  }
}

/** Part A–D ve sonrası: kabuğun içeriği. */
function sections(t: RecordPdfT, record: DrillRecordDetail, files: ReportFiles) {
  const f = (key: string) => t(`drillReport.fields.${key}`);
  const c = (key: string) => t(`drillReport.columns.${key}`);
  const v = (key: string, options?: Record<string, unknown>) =>
    t(`drillReport.values.${key}`, options);
  const sub = (key: string): Content => ({
    text: t(`drillReport.sub.${key}`),
    style: 'label',
    bold: true,
    margin: [0, 6, 0, 2],
  });
  const none = (key: string): Content => ({ text: t(`drillReport.none.${key}`), style: 'muted' });
  const yesNo = (value: boolean | null | undefined) =>
    value === true ? v('yes') : value === false ? v('no') : '—';
  const g = record.general;
  const present = record.participants.filter((p) => p.attendance === 'present').length;
  const count = (result: string) => record.steps.filter((s) => s.result === result).length;
  const stepResult = (result: string | null) =>
    result === 'completed'
      ? t('drillRecord.execution.completed')
      : result === 'not_completed'
        ? t('drillRecord.execution.notCompleted')
        : result === 'na'
          ? t('drillRecord.execution.na')
          : '—';
  const rating = (value: string | null | undefined) =>
    value === 'satisfactory'
      ? t('drillRecord.evaluation.satisfactory')
      : value === 'needs_improvement'
        ? t('drillRecord.evaluation.needsImprovement')
        : '—';

  /* Part A */
  const partA: Content[] = [
    pdfHeading(t('drillReport.parts.a')),
    pdfFacts([
      [f('vessel'), pdfText(record.vesselName)],
      [f('recordNo'), record.recordNo],
      [f('date'), formatDay(g.drillDate)],
      [f('time'), g.startTime ? `${g.startTime}–${g.endTime ?? '…'}` : '—'],
      [
        f('duration'),
        record.durationMinutes === null ? '—' : v('minutes', { count: record.durationMinutes }),
      ],
      [f('location'), pdfText(g.location)],
      [f('drill'), `${record.templateCode} · ${record.templateName}`],
      [f('category'), pdfText(record.categoryName)],
      [f('responsible'), pdfText(g.responsibleName)],
      [f('status'), t(`drillRecord.state.${record.state}`)],
    ]),
    ...pdfBlock(f('scenario'), g.scenario),
  ];
  if (record.stop) {
    const incident = record.incidents.find((i) => i.id === record.stop?.hseReportId);
    partA.push(
      sub('stop'),
      pdfFacts([
        [f('stopReason'), record.stop.reason],
        [f('stoppedAt'), pdfText(record.stop.stoppedTime)],
        [f('incident'), incident ? `${incident.number} · ${incident.title}` : '—'],
      ]),
    );
  }
  if (record.state === 'cancelled') partA.push(...pdfBlock(f('cancelReason'), record.cancelReason));

  /* Part B */
  const prep = record.preparation;
  const ra = record.raVerifications.find((r) => r.id === prep.raVerificationId);
  const partB: Content[] = [
    pdfHeading(t('drillReport.parts.b')),
    sub('preparation'),
    pdfFacts([
      [f('briefing'), yesNo(prep.briefingDone)],
      [f('equipmentReady'), yesNo(prep.equipmentReady)],
      [f('weather'), pdfText(prep.weather)],
      [f('constraints'), pdfText(prep.constraints)],
      [f('ra'), ra ? `${ra.number} · ${ra.title}` : '—'],
      [f('attendance'), v('presentCount', { present, total: record.participants.length })],
    ]),
    ...pdfBlock(f('briefingNotes'), prep.briefingNotes),
    ...pdfBlock(f('equipmentNotes'), prep.equipmentNotes),
    ...pdfBlock(f('riskNotes'), prep.riskNotes),
    sub('checklist'),
    prep.checklist.length === 0
      ? none('checklist')
      : pdfTable(
          [c('item'), c('done')],
          prep.checklist.map((item) => [item.item, yesNo(item.done)]),
          ['*', 60],
        ),
    sub('participants'),
    record.participants.length === 0
      ? none('participants')
      : pdfTable(
          [c('name'), c('rank'), c('role'), c('attendance'), c('absence')],
          record.participants.map((p) => [
            p.name,
            pdfText(p.rank),
            pdfText(p.role),
            p.attendance === 'present'
              ? v('present')
              : p.attendance === 'absent'
                ? v('absent')
                : '—',
            pdfText(p.absenceReason),
          ]),
          ['*', 70, 80, 55, '*'],
        ),
    sub('steps'),
    {
      text: v('stepsCount', {
        completed: count('completed'),
        notCompleted: count('not_completed'),
        na: count('na'),
        total: record.steps.length,
      }),
      style: 'muted',
      margin: [0, 0, 0, 2],
    },
    record.steps.length === 0
      ? none('steps')
      : pdfTable(
          [
            c('no'),
            c('step'),
            c('result'),
            c('time'),
            c('responsible'),
            c('remarks'),
            c('evidence'),
          ],
          record.steps.map((s) => [
            String(s.stepNo),
            s.text,
            stepResult(s.result),
            s.doneTime
              ? `${s.doneTime}${s.elapsedSeconds !== null ? ` (+${formatSeconds(s.elapsedSeconds)})` : ''}`
              : '—',
            pdfText(s.responsibleName),
            pdfText(s.remarks),
            s.attachments > 0 ? v('evidence', { count: s.attachments }) : '—',
          ]),
          [18, '*', 52, 52, 62, '*', 38],
        ),
  ];

  /* Part C */
  const results = new Map(record.evaluation.results.map((r) => [r.key, r]));
  const score = drillEvaluationScore(record.criteria, record.evaluation.results);
  const crewRatings = record.evaluation.crew ?? [];
  const nameOf = (userId: string) =>
    record.participants.find((p) => p.userId === userId)?.name ?? userId;
  const deficiencies: string[] = [];
  if (prep.equipmentReady === false) {
    deficiencies.push(
      t('drillReport.deficiency.equipment', { note: pdfText(prep.equipmentNotes) }),
    );
  }
  for (const s of record.steps) {
    if (s.result === 'not_completed') {
      deficiencies.push(
        t('drillReport.deficiency.step', {
          no: s.stepNo,
          text: s.remarks ? `${s.text} — ${s.remarks}` : s.text,
        }),
      );
    }
  }
  for (const criterion of record.criteria) {
    if (results.get(criterion.key)?.rating === 'needs_improvement') {
      deficiencies.push(t('drillReport.deficiency.criterion', { criterion: criterion.label }));
    }
  }
  const partC: Content[] = [
    pdfHeading(t('drillReport.parts.c')),
    sub('criteria'),
    pdfTable(
      [c('criterion'), c('value'), c('note')],
      record.criteria.map((criterion) => {
        const result = results.get(criterion.key);
        return [
          criterion.label,
          criterion.kind === 'time'
            ? typeof result?.seconds === 'number'
              ? formatSeconds(result.seconds)
              : '—'
            : rating(result?.rating),
          pdfText(result?.note),
        ];
      }),
      [130, 90, '*'],
    ),
    pdfFacts([
      [
        f('score'),
        score.percent === null
          ? v('noScore')
          : v('score', {
              percent: score.percent,
              satisfactory: score.satisfactory,
              rated: score.rated,
            }),
      ],
    ]),
    ...pdfBlock(f('debriefing'), record.evaluation.debriefing),
    sub('crew'),
    crewRatings.length === 0
      ? none('crew')
      : pdfTable(
          [c('name'), c('rating'), c('note')],
          crewRatings.map((r) => [nameOf(r.userId), rating(r.rating), pdfText(r.note)]),
          [130, 90, '*'],
        ),
    sub('deficiencies'),
    deficiencies.length === 0 ? none('deficiencies') : { ul: deficiencies, margin: [0, 0, 0, 2] },
  ];

  /* Part D */
  const findings = record.findings ?? [];
  const partD: Content[] = [
    pdfHeading(t('drillReport.parts.d')),
    findings.length === 0
      ? none('findings')
      : pdfTable(
          [
            c('finding'),
            c('type'),
            c('description'),
            c('responsible'),
            c('due'),
            c('linked'),
            c('status'),
          ],
          findings.map((finding) => {
            const link = finding.link;
            const follow = [
              link ? `${link.no} · ${link.status.replaceAll('_', ' ')}` : '—',
              ...(link && link.workOrders.length > 0
                ? [
                    v('workOrders', {
                      list: link.workOrders.map((w) => `${w.woNo} (${w.status})`).join(', '),
                    }),
                  ]
                : []),
              ...(link && link.requisitions.length > 0
                ? [
                    v('requisitions', {
                      list: link.requisitions
                        .map((r) => `${r.requisitionNo} (${r.status})`)
                        .join(', '),
                    }),
                  ]
                : []),
            ].join('\n');
            const description = [
              finding.description,
              finding.personName ? `(${finding.personName})` : null,
              finding.redrill ? v('redrill') : null,
              finding.cancelReason ? `— ${finding.cancelReason}` : null,
            ]
              .filter(Boolean)
              .join(' ');
            return [
              finding.findingNo,
              t(`drillFinding.types.${finding.type}`),
              description,
              pdfText(finding.responsibleName),
              finding.dueOn ? formatDay(finding.dueOn) : '—',
              follow,
              `${t(`drillFinding.status.${finding.status}`)}${finding.overdue ? ` · ${t('drillFinding.overdue')}` : ''}`,
            ];
          }),
          [62, 55, '*', 58, 48, 92, 48],
        ),
  ];

  /* Şablon ve sürüm, ekler, değişiklik geçmişi */
  const attachments = record.attachmentList ?? [];
  const trail = record.auditTrail ?? [];
  const kind = (key: string) => t(`drillReport.audit.kinds.${key}`);
  const trailer: Content[] = [
    pdfHeading(t('drillReport.parts.version')),
    pdfFacts([
      [f('template'), `${record.templateCode} · ${record.templateName}`],
      [f('templateVersion'), v('version', { version: record.templateVersion })],
      [
        f('library'),
        record.libraryVersion !== record.templateVersion
          ? v('libraryNow', { version: record.libraryVersion, pinned: record.templateVersion })
          : v('libraryCurrent', { version: record.libraryVersion }),
      ],
      [f('vesselScenario'), record.variantDecisionId ? v('scenarioYes') : v('scenarioNo')],
      [f('contentHash'), record.contentHash],
    ]),
    { text: t('drillReport.versionNote'), style: 'muted', margin: [0, 2, 0, 0] },
    pdfHeading(t('drillReport.parts.attachments')),
    ...reportAttachmentSection(t, attachments, files, {
      prefix: 'drillReport',
      ref: (a) => a.stepNo,
    }),
    pdfHeading(t('drillReport.parts.history')),
    trail.length === 0
      ? none('history')
      : pdfTable(
          [c('at'), c('event'), c('by'), c('detail')],
          trail.map((e) => [
            utc(e.at),
            kind(e.kind),
            `${pdfText(e.byName)}${e.capacity === 'deputy' ? ` (${t('drillReport.approval.deputy')})` : ''}`,
            [
              e.sections.map((s) => t(`drillReport.audit.sections.${s}`)).join(', '),
              e.labels.join(', '),
              e.reason ?? '',
            ]
              .filter(Boolean)
              .join(' · ') || '—',
          ]),
          [90, 120, 110, '*'],
        ),
  ];
  return { content: [...partA, ...partB, ...partC, ...partD], trailer };
}

/** Part E: gönderimler ve kaptan kararları (vekil "on behalf of Master"). */
function approvals(t: RecordPdfT, record: DrillRecordDetail): RecordPdfApproval[] {
  const a = (key: string, options?: Record<string, unknown>) =>
    t(`drillReport.approval.${key}`, options);
  const out: RecordPdfApproval[] = [];
  for (const event of record.events) {
    if (event.kind === 'submitted') {
      out.push({
        step: a('submitted'),
        name: event.byName,
        role: a('responsibleRole'),
        at: event.at,
        offsetMin: record.tzOffsetMin,
        note: event.contentHash ? a('hash', { hash: shortHash(event.contentHash) }) : null,
      });
      if (event.decision) {
        out.push({
          step: a('decision', {
            decision: t(`drillRecord.decisionValue.${event.decision.decision}`),
          }),
          name: event.decision.decidedByName,
          role: event.decision.capacity === 'deputy' ? a('deputy') : a('master'),
          at: event.decision.decidedAt,
          offsetMin: record.tzOffsetMin,
          note: event.decision.reason,
        });
      }
    } else if (event.kind === 'reopened') {
      out.push({
        step: a('reopened'),
        name: event.byName,
        role: event.capacity === 'deputy' ? a('deputy') : a('master'),
        at: event.at,
        offsetMin: record.tzOffsetMin,
        note: event.reason,
      });
    }
  }
  return out;
}

export function drillReportPdf(t: RecordPdfT, input: DrillReportInput): TDocumentDefinitions {
  const { record } = input;
  const { content, trailer } = sections(t, input.record, input.files ?? {});
  return recordPdf(t, {
    brand: input.brand,
    title: t('drillReport.title'),
    number: record.recordNo,
    marks: marks(t, record),
    subject: `${record.vesselName ?? ''} · ${record.templateCode} · ${record.general.drillDate}`,
    content,
    approvalTitle: t('drillReport.parts.e'),
    approvals: approvals(t, record),
    trailer,
    printedAt: input.printedAt,
  });
}

/**
 * Toplu PDF (Records arşivi): her kayıt kendi Part A–E'siyle yeni sayfada; altbilgi genel.
 */
export function drillReportsPdf(
  t: RecordPdfT,
  inputs: readonly DrillReportInput[],
  printedAt: string,
): TDocumentDefinitions {
  const definitions = inputs.map((input) => drillReportPdf(t, input));
  const [first] = definitions;
  if (!first) throw new Error('EMPTY_EXPORT');
  const content: Content[] = [];
  definitions.forEach((definition, index) => {
    const nodes = definition.content as Content[];
    if (index > 0) content.push({ text: '', pageBreak: 'before' });
    content.push(...nodes);
  });
  const title = t('drillReport.bulkTitle');
  const printed = t('recordPdf.printed', { at: utc(printedAt) });
  return {
    ...first,
    info: { title, subject: title, creator: 'Felixmate' },
    content,
    footer: (page: number, pages: number) => ({
      columns: [
        { text: `${title} · ${printed} · Felixmate`, fontSize: 7 },
        {
          text: t('recordPdf.page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

/**
 * Dosya adının güvenli gövdesi: yalnız ASCII harf, rakam, nokta, tire ve alt çizgi; boşluk yok.
 * Gemide ana süreç bu kalıp dışındaki adı reddeder (`saved-file.ts`); dil bağımsızdır.
 */
export function safeFileBase(text: string): string {
  return (
    text
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9._-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[-._]+|[-._]+$/g, '')
      .slice(0, 120) || 'drill'
  );
}

/** Dosya adı: "drill-report-DR-2026-004-DRL-FIRE-01.pdf". */
export function drillReportFileName(
  record: Pick<DrillRecordDetail, 'recordNo' | 'templateCode'>,
): string {
  return `${safeFileBase(`drill-report-${record.recordNo}-${record.templateCode}`)}.pdf`;
}

export interface DrillRecordsManifest {
  format: 'felix-drill-records/1';
  generatedAt: string;
  records: {
    recordNo: string;
    vessel: string | null;
    drill: string;
    drillDate: string;
    state: string;
    templateVersion: number;
    contentHash: string;
    file: string;
    size: number;
    sha256: string;
  }[];
}

/**
 * ZIP arşivi (yakıt dönem paketi emsali, ADR 0117 Ek Y7): kayıt başına PDF ve dosyaların SHA-256'sı
 * ile kaydın içerik hash'ini taşıyan `manifest.json`. İstemcide, sıkıştırmasız; yeni bağımlılık yok.
 */
export async function drillReportsZip(
  t: RecordPdfT,
  inputs: readonly DrillReportInput[],
  generatedAt: string,
  render: (definition: TDocumentDefinitions) => Promise<Uint8Array> = renderPdf,
  onProgress?: (done: number) => void,
): Promise<{ zip: Uint8Array; manifest: DrillRecordsManifest }> {
  const files: { name: string; bytes: Uint8Array }[] = [];
  const records: DrillRecordsManifest['records'] = [];
  const used = new Set<string>();
  for (const input of inputs) {
    const { record } = input;
    let name = drillReportFileName(record);
    for (let n = 2; used.has(name); n += 1) name = name.replace(/(-\d+)?\.pdf$/, `-${n}.pdf`);
    used.add(name);
    const bytes = await render(drillReportPdf(t, input));
    files.push({ name, bytes });
    records.push({
      recordNo: record.recordNo,
      vessel: record.vesselName,
      drill: `${record.templateCode} · ${record.templateName}`,
      drillDate: record.general.drillDate,
      state: record.state,
      templateVersion: record.templateVersion,
      contentHash: record.contentHash,
      file: name,
      size: bytes.length,
      sha256: sha256Hex(bytes),
    });
    onProgress?.(files.length);
  }
  const manifest: DrillRecordsManifest = { format: 'felix-drill-records/1', generatedAt, records };
  const json = new TextEncoder().encode(`${JSON.stringify(manifest, null, 2)}\n`);
  return { zip: zipStore([...files, { name: 'manifest.json', bytes: json }]), manifest };
}
